// @ts-strict-ignore
import { MetaElementBehaviorPropertyDef } from '@project/constants/meta-element-behavior-property-def';
import { PropertyUtils } from '../../../util/property-utils';
import { ArticleVO } from '../../../model/vo/article.vo';
import { ConnectionAreaVO, ConnectionFreedomVO, MetaElementVO, TextureAreaVO } from '@shared/vo/meta-element.vo';
import { PropertyVO } from '@project/components/article/model/vo/property.vo';
import { ConnectionDefinition } from './connection-configuration';
import { ConnectionAreaType } from './connection-creator';
import { Dictionary } from 'typescript-collections';
import { StringUtils } from '@shared/util/string-utils';

export class MetaElementUtils {
  public static readonly INTERNAL_CONNECTION_POSTFIX: string = '_internal';

  public static isMultipurposeArea(metaElement: MetaElementVO): boolean {
    return !!this.getMultipurposeConnectionSetId(metaElement);
  }

  public static getMultipurposeConnectionSetId(metaElement: MetaElementVO): string {
    if (!metaElement) {
      return null;
    }
    return PropertyUtils.getStringPropertyValue(
      metaElement.behaviorProperties,
      MetaElementBehaviorPropertyDef.MULTIPURPOSE_CONNECTION_SET_ID);
  }

  public static setMultipurposeConnectionSetId(metaElement: MetaElementVO, multipurposeSetId: string): void {
    if (!metaElement || !multipurposeSetId) {
      return;
    }
    if (!metaElement.behaviorProperties) {
      metaElement.behaviorProperties = [];
    }
    PropertyUtils.setStringPropertyValue(metaElement.behaviorProperties, MetaElementBehaviorPropertyDef.MULTIPURPOSE_CONNECTION_SET_ID, multipurposeSetId);
  }

  public static applySetIdToMultipurposeAreas(mainMetaElement: MetaElementVO, internalMetaElement: MetaElementVO): string {
    if (!mainMetaElement || !internalMetaElement) {
      return null;
    }
    return this.applySetIdToMultipurposeAreasAsArray([mainMetaElement, internalMetaElement]);
  }

  public static applySetIdToMultipurposeAreasAsArray(metaElements: MetaElementVO[]): string {
    if (metaElements && metaElements.length > 1) {
      const setId: string = StringUtils.createUuid();
      for (const metaElement of metaElements) {
        this.setMultipurposeConnectionSetId(metaElement, setId);
      }
      return setId;
    }
  }

  public static removeSetIdFromMultipurposeArea(metaElement: MetaElementVO): void {
    if (!metaElement) {
      return;
    }
    PropertyUtils.removePropertiesByName(metaElement.behaviorProperties, MetaElementBehaviorPropertyDef.MULTIPURPOSE_CONNECTION_SET_ID);
  }

  public static getConnectionOrderNumberAndCorridorForDisplayPurposes(metaElement: MetaElementVO): string {
    const orderNumber: string = MetaElementUtils.getMetaElementOrderNumberForDisplayPurposes(metaElement);
    const corridor: string = MetaElementUtils.getMetaElementCorridorForDisplayPurposes(metaElement);
    if (corridor) {
      return `${orderNumber} / ${corridor}`;
    } else {
      return orderNumber;
    }
  }

  public static getConnectionTypeForDisplayPurposes(connection: MetaElementVO): string {
    if (connection instanceof ConnectionAreaVO) {
      if (MetaElementUtils.isMultipurposeArea(connection)) {
        return 'MULTI';
      } else {
        return connection.connectionSide;
      }
    } else if (connection instanceof TextureAreaVO) {
      return connection.textureType;
    }
  }

  public static getMetaElementOrderNumberForDisplayPurposes(metaElement: MetaElementVO): string {
    if (!metaElement) {
      return '';
    }
    return metaElement.orderNumber || metaElement.orderNumber === 0
      ? metaElement.orderNumber.toString()
      : '';
  }

  public static getMetaElementCorridorForDisplayPurposes(metaElement: MetaElementVO): string {
    if (!metaElement) {
      return '';
    }
    return metaElement.corridor || metaElement.corridor === 0
      ? metaElement.corridor.toString()
      : '';
  }

  public static hasInternalConnectionPostfix(metaElementName: string): boolean {
    if (metaElementName) {
      return metaElementName.endsWith(MetaElementUtils.INTERNAL_CONNECTION_POSTFIX);
    }
    return false;
  }

  public static applyInternalConnectionPostfix(metaElementName: string): string {
    if (metaElementName) {
      metaElementName += MetaElementUtils.INTERNAL_CONNECTION_POSTFIX;
    }
    return metaElementName;
  }

  public static removeInternalConnectionPostfix(metaElementName: string): string {
    if (!metaElementName) {
      return null;
    }
    const internalConnectionPostfixIndex: number = metaElementName.lastIndexOf(MetaElementUtils.INTERNAL_CONNECTION_POSTFIX);
    return internalConnectionPostfixIndex === -1 ? metaElementName : metaElementName.substring(0, internalConnectionPostfixIndex);
  }

  public static isImageAllowed(metaElement: MetaElementVO): boolean {
    if (!metaElement) {
      return false;
    }
    return metaElement instanceof TextureAreaVO && this.isImageAllowedBySubtype((metaElement as TextureAreaVO).textureType);
  }

  public static isImageAllowedBySubtype(subtype: string): boolean {
    return subtype === TextureAreaVO.TYPE_IMAGE;
  }

  public static createMetaElementByName(article: ArticleVO, connectionDefinition: ConnectionDefinition, multiplicity: number,
    bothsides: boolean): MetaElementVO {
    if (!connectionDefinition) {
      return null;
    }
    let createdMetaElement: MetaElementVO = null;
    if (connectionDefinition.type === ConnectionAreaType.CONNECTION_AREA) {
      const connectionArea: ConnectionAreaVO = new ConnectionAreaVO();
      connectionArea.name = connectionDefinition.name;
      connectionArea.connectionFeatures = [];
      connectionArea.connectionRequirements = [];
      connectionArea.connectionPlausiProperties = [];
      connectionArea.behaviorProperties = [];
      connectionArea.connectionConstructionProperties = [];
      connectionArea.corridor = 0;
      connectionArea.orderNumber = article.metaElements.length === 0 ? 1 : Math.max(...article.metaElements.filter((meta: MetaElementVO) => !meta.isInternal).map((meta: MetaElementVO) => meta == null ? 0 : meta.orderNumber)) + 1;
      connectionArea.connectionMultiplicity = multiplicity;
      connectionArea.bothsides = bothsides;

      const freedom: ConnectionFreedomVO = new ConnectionFreedomVO();
      freedom.nFreedom = 1;
      freedom.uFreedom = 1;
      freedom.vFreedom = 1;
      connectionArea.connectionFreedom = freedom;

      createdMetaElement = connectionArea;
    } else if (connectionDefinition.type === ConnectionAreaType.TEXTURE_AREA) {
      const textureArea: TextureAreaVO = new TextureAreaVO();
      textureArea.name = connectionDefinition.name;
      textureArea.corridor = NaN;
      textureArea.orderNumber = 0;
      textureArea.behaviorProperties = [];
      createdMetaElement = textureArea;
    }
    if (createdMetaElement) {
      this.applySubtypeToMetaElement(createdMetaElement, connectionDefinition.subtype);
      createdMetaElement.isInternal = connectionDefinition.isInternal;
    }
    return createdMetaElement;
  }

  public static excludeInternalMetaElements(metaElements: MetaElementVO[]): MetaElementVO[] {
    return !metaElements ? [] : metaElements.filter((meta: MetaElementVO) => !meta.isInternal);
  }

  public static applySubtypeToMetaElement(metaElement: MetaElementVO, subtype: string): void {
    if (!metaElement) {
      return;
    }
    if (metaElement instanceof ConnectionAreaVO) {
      metaElement.connectionSide = subtype ? subtype : ConnectionAreaVO.SIDE_ACCEPTING;
    } else if (metaElement instanceof TextureAreaVO) {
      metaElement.textureType = subtype ? subtype : TextureAreaVO.TYPE_TEXT;
    }
  }

  public static addMetaElementToArticle(article: ArticleVO, metaElement: MetaElementVO): void {
    if (!article || !metaElement) {
      return;
    }
    if (!article.metaElements) {
      article.metaElements = [];
    }
    article.metaElements.push(metaElement);
  }

  public static replacementPropertiesHandler(targetMeta: PropertyVO[], sourceMeta: PropertyVO[], isUndoOperation: boolean = false): void {
    if (isUndoOperation) {
      targetMeta.length = 0;
      sourceMeta.forEach((property: PropertyVO) => {
        targetMeta.push(property);
      });
    } else if (!isUndoOperation && sourceMeta.length) {
      sourceMeta.forEach((value: PropertyVO) => {
        const commonProperty: PropertyVO = targetMeta.find((propertyVO: PropertyVO) => {
          return value.name === propertyVO.name;
        });

        if (commonProperty) {
          targetMeta.splice(targetMeta.indexOf(commonProperty), 1, value);
        } else {
          targetMeta.push(value);
        }
      });
    }
  }

    public static getConnectionsInSets(metaElements: MetaElementVO[]): Dictionary<string, MetaElementVO[]> {
        const result: Dictionary<string, MetaElementVO[]> = new Dictionary<string, MetaElementVO[]>();
        let connectionSetId: string;
        let connectionSetValues: MetaElementVO[];
        for (const metaElement of metaElements) {
            connectionSetId = this.getConnectionSetId(metaElement);
            connectionSetValues = result.getValue(connectionSetId);
            if (!connectionSetValues) {
                connectionSetValues = [metaElement];
            } else {
                connectionSetValues.push(metaElement);
            }
            result.setValue(connectionSetId, connectionSetValues);
        }
        return result;
    }

    public static getConnectionSetId(metaElement: MetaElementVO): string {
        if (!metaElement) {
            return null;
        }
        return PropertyUtils.getStringPropertyValue(metaElement.behaviorProperties, MetaElementBehaviorPropertyDef.CONNECTION_SET_ID);
    }

  public static isConnectedArticleNotDeletable(metaElement: MetaElementVO): boolean {
    if (!metaElement) {
      return true;
    }
    return PropertyUtils.getBooleanPropertyValue(metaElement.behaviorProperties, MetaElementBehaviorPropertyDef.CONNECTED_ARTICLE_IS_NOT_DELETABLE);
  }

  public static isDyeable(metaElement: MetaElementVO): boolean {
    if (!metaElement) {
      return false;
    }
    return PropertyUtils.getBooleanPropertyValue(metaElement.behaviorProperties, MetaElementBehaviorPropertyDef.IS_DYEABLE, false);
  }
}
